/**
 * Job notifications — STOMP/SockJS client for backend batch-job lifecycle events.
 *
 * The backend JobCompletionListener publishes JobNotification payloads on
 * `/topic/jobs/{tenantId}` (STARTED in beforeJob, COMPLETED/FAILED in afterJob).
 *
 * Design:
 * - Lazy singleton connection (connects on first tracked job / subscriber),
 *   built by the shared realtime client (UPT-1814) : le CONNECT porte le jeton
 *   JWT Clerk des appels REST, reconnexion à délai croissant, abandon après
 *   refus répétés ou endpoint injoignable (UPT-1512)
 * - Tenant-scoped topic (UPT-1814) : `/topic/jobs/{tenantId}` avec le tenant
 *   courant — même source que les en-têtes tenant des appels REST
 *   (resolveSaasContext) ; ré-abonnement au changement de tenant (contexte
 *   d'auth backend ou override de tenant, les deux sources de useTenantId)
 * - Session-scoped filtering: only jobs launched via `trackLaunchedJob()`
 *   in this browser session are dispatched to listeners
 */

import type { IMessage, StompSubscription } from '@stomp/stompjs';
import { createRealtimeClient, jobsTopic, type RealtimeClient } from '@/uptk/core/realtime/stompClient';
import { resolveSaasContext } from '@/uptk/core/api/saasContext';
import { subscribeAuthContextState } from '@/uptk/core/api/auth_context_store';
import { subscribeTenantOverrideState } from '@/uptk/core/api/tenant_override_store';

export interface JobNotification {
  jobId: number;
  jobName: string;
  status: string;
  exitCode: string | null;
  exitDescription: string | null;
  tenantId: string | null;
  workspaceId: string | null;
  email: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

type JobNotificationListener = (notification: JobNotification) => void;

const launchedJobIds = new Set<string>();
const listeners = new Set<JobNotificationListener>();

// Anti-race buffer: a fast-crashing job can publish its final FAILED/COMPLETED
// event BEFORE the validate HTTP response returns and trackLaunchedJob() runs.
// Final-state events for unknown job ids are kept briefly and flushed when the
// job id is registered, so fast failures are never silently dropped (UPT-1060).
const FINAL_STATES = new Set(['COMPLETED', 'FAILED', 'STOPPED']);
const PENDING_EVENT_TTL_MS = 60_000;
const PENDING_EVENT_MAX = 50;
const pendingEvents: Array<{ receivedAt: number; notification: JobNotification }> = [];

function prunePendingEvents(): void {
  const cutoff = Date.now() - PENDING_EVENT_TTL_MS;
  while (pendingEvents.length > 0 && pendingEvents[0].receivedAt < cutoff) {
    pendingEvents.shift();
  }
}

function dispatch(notification: JobNotification): void {
  listeners.forEach((listener) => listener(notification));
}

let realtime: RealtimeClient | null = null;
let jobsSubscription: StompSubscription | null = null;
let subscribedTenantId = '';
let tenantWatchersInstalled = false;

/** Tenant courant — même source que les en-têtes tenant des appels REST (buildSaasHeaders(resolveSaasContext())). */
function currentTenantId(): string {
  return resolveSaasContext().tenantId ?? '';
}

function onJobMessage(message: IMessage): void {
  try {
    const notification = JSON.parse(message.body) as JobNotification;
    if (launchedJobIds.has(String(notification.jobId))) {
      dispatch(notification);
      return;
    }
    if (FINAL_STATES.has(notification.status)) {
      prunePendingEvents();
      if (pendingEvents.length >= PENDING_EVENT_MAX) pendingEvents.shift();
      pendingEvents.push({ receivedAt: Date.now(), notification });
    }
  } catch (error) {
    console.warn('[jobNotifications] malformed job event', error);
  }
}

function dropJobsSubscription(): void {
  if (jobsSubscription) {
    try {
      jobsSubscription.unsubscribe();
    } catch (error) {
      console.warn('[jobNotifications] unsubscribe failed', error);
    }
  }
  jobsSubscription = null;
  subscribedTenantId = '';
}

/**
 * (Ré)abonne le topic de jobs du tenant courant. Sans effet hors connexion
 * (l'abonnement est refait à chaque CONNECT) ; un tenant inconnu diffère
 * l'abonnement jusqu'au prochain événement de contexte.
 */
function subscribeJobsTopic(): void {
  const stomp = realtime?.stomp;
  if (!stomp?.connected) return;
  const tenantId = currentTenantId();
  if (jobsSubscription && subscribedTenantId === tenantId) return;
  dropJobsSubscription();
  if (!tenantId) {
    console.warn('[jobNotifications] tenant inconnu — abonnement aux notifications de jobs différé');
    return;
  }
  jobsSubscription = stomp.subscribe(jobsTopic(tenantId), onJobMessage);
  subscribedTenantId = tenantId;
}

/** Ré-abonnement au changement de tenant : mêmes sources que useTenantId. */
function installTenantWatchers(): void {
  if (tenantWatchersInstalled) return;
  tenantWatchersInstalled = true;
  subscribeAuthContextState(() => subscribeJobsTopic());
  subscribeTenantOverrideState(() => subscribeJobsTopic());
}

function ensureConnected(): void {
  if (realtime == null) {
    realtime = createRealtimeClient({
      name: 'jobNotifications',
      onConnect: () => {
        // Les abonnements de la session précédente sont perdus avec le socket.
        jobsSubscription = null;
        subscribedTenantId = '';
        subscribeJobsTopic();
      },
      onDisconnect: () => {
        jobsSubscription = null;
        subscribedTenantId = '';
      },
    });
    installTenantWatchers();
  }
  realtime.ensureConnected();
}

/** Whether the STOMP connection is currently established. */
export function isJobSocketConnected(): boolean {
  return realtime?.isConnected() ?? false;
}

/**
 * Register a job launched in this session (e.g. fileloading validate).
 * Only tracked jobs dispatch notifications to listeners.
 */
export function trackLaunchedJob(jobId: unknown): void {
  if (jobId == null) return;
  const key = String(jobId);
  launchedJobIds.add(key);
  // Flush any final-state event that arrived before the job id was registered.
  prunePendingEvents();
  for (let i = pendingEvents.length - 1; i >= 0; i--) {
    if (String(pendingEvents[i].notification.jobId) === key) {
      dispatch(pendingEvents[i].notification);
      pendingEvents.splice(i, 1);
    }
  }
  ensureConnected();
}

/**
 * Subscribe to lifecycle events of session-launched jobs.
 * Returns an unsubscribe function.
 */
export function subscribeJobNotifications(listener: JobNotificationListener): () => void {
  listeners.add(listener);
  ensureConnected();
  return () => { listeners.delete(listener); };
}
